import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { vehicleAPI } from '../../services/api';
import { Badge, Button, Spinner, Alert, Modal, Form } from 'react-bootstrap';
import {
  FiPlus, FiCopy, FiExternalLink, FiEdit2, FiTrash2,
  FiSearch, FiUploadCloud, FiTruck,
} from 'react-icons/fi';

interface Vehicle {
  id: number;
  plateNumber: string;
  vehicleMake?: string;
  vehicleModel?: string;
  year?: number;
  color?: string;
  licenseExpiryDate?: string;
  insuranceExpiryDate?: string;
  isActive: boolean;
}

const VehiclesPage: React.FC = () => {
  const [vehicles, setVehicles]               = useState<Vehicle[]>([]);
  const [loading, setLoading]                 = useState(true);
  const [error, setError]                     = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [vehicleToDelete, setVehicleToDelete] = useState<Vehicle | null>(null);
  const [searchTerm, setSearchTerm]           = useState('');
  const [selectedIds, setSelectedIds]         = useState<Set<number>>(new Set());
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [bulkDeleting, setBulkDeleting]       = useState(false);
  const [currentPage, setCurrentPage]         = useState(1);
  const [copiedId, setCopiedId]               = useState<number | null>(null);
  const [copiedAll, setCopiedAll]             = useState(false);
  const PAGE_SIZE = 10;

  useEffect(() => { fetchVehicles(); }, []);

  const fetchVehicles = async () => {
    try {
      const response = await vehicleAPI.getAllVehicles();
      setVehicles(response.data);
    } catch {
      setError('Failed to load vehicles');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!vehicleToDelete) return;
    try {
      await vehicleAPI.deleteVehicle(vehicleToDelete.id);
      setVehicles(vehicles.filter(v => v.id !== vehicleToDelete.id));
      setSelectedIds(prev => { const s = new Set(prev); s.delete(vehicleToDelete.id); return s; });
      setShowDeleteModal(false);
      setVehicleToDelete(null);
    } catch {
      setError('Failed to delete vehicle');
    }
  };

  const handleBulkDelete = async () => {
    setBulkDeleting(true);
    try {
      await vehicleAPI.bulkDeleteVehicles(Array.from(selectedIds));
      setVehicles(vehicles.filter(v => !selectedIds.has(v.id)));
      setSelectedIds(new Set());
      setShowBulkDeleteModal(false);
    } catch {
      setError('Failed to delete selected vehicles');
    } finally {
      setBulkDeleting(false);
    }
  };

  const toggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds(
      selectedIds.size === filteredVehicles.length
        ? new Set()
        : new Set(filteredVehicles.map(v => v.id))
    );
  };

  const vehicleLink = (plateNumber: string) =>
    `${window.location.origin}/car?plate=${plateNumber}`;

  const copyLink = (vehicle: Vehicle) => {
    navigator.clipboard.writeText(vehicleLink(vehicle.plateNumber));
    setCopiedId(vehicle.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const copyAllLinks = () => {
    const text = filteredVehicles.map(v => `${v.plateNumber}: ${vehicleLink(v.plateNumber)}`).join('\n');
    navigator.clipboard.writeText(text);
    setCopiedAll(true);
    setTimeout(() => setCopiedAll(false), 2000);
  };

  const isExpired = (date?: string) => date ? new Date(date) < new Date() : false;

  const filteredVehicles = vehicles.filter(v =>
    v.plateNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.vehicleMake?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.vehicleModel?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages   = Math.ceil(filteredVehicles.length / PAGE_SIZE);
  const pagedVehicles = filteredVehicles.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}>
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <>
      <div className="page-header">
        <h2>Vehicles</h2>
        <div className="d-flex gap-2 flex-wrap">
          {selectedIds.size > 0 && (
            <Button variant="danger" className="d-flex align-items-center gap-2"
              onClick={() => setShowBulkDeleteModal(true)}>
              <FiTrash2 /> Delete ({selectedIds.size})
            </Button>
          )}
          <Button variant="outline-secondary" className="d-flex align-items-center gap-2" onClick={copyAllLinks}>
            <FiCopy /> {copiedAll ? 'Copied!' : `Copy All (${filteredVehicles.length})`}
          </Button>
          <Link to="/admin/bulk-import">
            <Button variant="outline-secondary" className="d-flex align-items-center gap-2">
              <FiUploadCloud /> Bulk Import
            </Button>
          </Link>
          <Link to="/admin/vehicles/new">
            <Button variant="primary" className="d-flex align-items-center gap-2">
              <FiPlus /> Add Vehicle
            </Button>
          </Link>
        </div>
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

      <div className="section-card">
        {/* Search bar */}
        <div className="section-card-header">
          <div style={{ position: 'relative', flex: 1, maxWidth: 400 }}>
            <span style={{
              position: 'absolute', left: '0.75rem', top: '50%',
              transform: 'translateY(-50%)', color: '#9ca3af',
            }}>
              <FiSearch />
            </span>
            <Form.Control
              type="text"
              placeholder="Search by plate, make or model…"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              style={{ paddingLeft: '2.25rem' }}
            />
          </div>
          <span style={{ fontSize: '0.82rem', color: '#64748b', whiteSpace: 'nowrap' }}>
            {filteredVehicles.length} vehicle{filteredVehicles.length !== 1 ? 's' : ''}
          </span>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table className="table mb-0">
            <thead>
              <tr>
                <th style={{ width: 40 }}>
                  <Form.Check
                    type="checkbox"
                    checked={filteredVehicles.length > 0 && selectedIds.size === filteredVehicles.length}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th>Plate</th>
                <th>Make</th>
                <th>Model</th>
                <th>Year</th>
                <th>Color</th>
                <th>License Exp.</th>
                <th>Insurance Exp.</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pagedVehicles.map((vehicle) => (
                <tr key={vehicle.id} className={selectedIds.has(vehicle.id) ? 'table-active' : ''}>
                  <td>
                    <Form.Check
                      type="checkbox"
                      checked={selectedIds.has(vehicle.id)}
                      onChange={() => toggleSelect(vehicle.id)}
                    />
                  </td>
                  <td>
                    <strong style={{ fontFamily: 'monospace', letterSpacing: '0.05em', color: '#1e293b' }}>
                      {vehicle.plateNumber}
                    </strong>
                  </td>
                  <td>{vehicle.vehicleMake   || <span className="text-muted">—</span>}</td>
                  <td>{vehicle.vehicleModel  || <span className="text-muted">—</span>}</td>
                  <td>{vehicle.year          || <span className="text-muted">—</span>}</td>
                  <td>{vehicle.color         || <span className="text-muted">—</span>}</td>
                  <td>
                    {vehicle.licenseExpiryDate ? (
                      <span>
                        {new Date(vehicle.licenseExpiryDate).toLocaleDateString()}
                        {isExpired(vehicle.licenseExpiryDate) && (
                          <Badge bg="danger" className="ms-1">Expired</Badge>
                        )}
                      </span>
                    ) : <span className="text-muted">—</span>}
                  </td>
                  <td>
                    {vehicle.insuranceExpiryDate ? (
                      <span>
                        {new Date(vehicle.insuranceExpiryDate).toLocaleDateString()}
                        {isExpired(vehicle.insuranceExpiryDate) && (
                          <Badge bg="danger" className="ms-1">Expired</Badge>
                        )}
                      </span>
                    ) : <span className="text-muted">—</span>}
                  </td>
                  <td>
                    {vehicle.isActive
                      ? <Badge bg="success">Active</Badge>
                      : <Badge bg="secondary">Inactive</Badge>}
                  </td>
                  <td>
                    <div className="action-btn-group">
                      <Button
                        variant="outline-secondary" size="sm"
                        onClick={() => copyLink(vehicle)}
                        title={vehicleLink(vehicle.plateNumber)}
                        className="d-flex align-items-center gap-1"
                      >
                        <FiCopy size={11} />
                        {copiedId === vehicle.id ? '✓' : 'Copy'}
                      </Button>
                      <Button
                        variant="outline-success" size="sm"
                        onClick={() => window.open(vehicleLink(vehicle.plateNumber), '_blank')}
                        className="d-flex align-items-center gap-1"
                      >
                        <FiExternalLink size={11} /> Open
                      </Button>
                      <Link to={`/admin/vehicles/${vehicle.id}`}>
                        <Button variant="outline-primary" size="sm" className="d-flex align-items-center gap-1">
                          <FiEdit2 size={11} /> Edit
                        </Button>
                      </Link>
                      <Button
                        variant="outline-danger" size="sm"
                        onClick={() => { setVehicleToDelete(vehicle); setShowDeleteModal(true); }}
                        className="d-flex align-items-center gap-1"
                      >
                        <FiTrash2 size={11} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredVehicles.length === 0 && (
            <div className="text-center py-5 text-muted">
              <FiTruck size={32} className="mb-2 d-block mx-auto" style={{ opacity: 0.3 }} />
              {searchTerm ? 'No vehicles match your search' : 'No vehicles found'}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="d-flex justify-content-between align-items-center px-3 py-2"
            style={{ borderTop: '1px solid #e2e8f0' }}>
            <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
              {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filteredVehicles.length)} of {filteredVehicles.length}
            </span>
            <div className="d-flex gap-1 pagination-bar">
              <Button size="sm" variant="outline-secondary" disabled={currentPage === 1} onClick={() => setCurrentPage(1)}>«</Button>
              <Button size="sm" variant="outline-secondary" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>‹</Button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                .reduce<(number | string)[]>((acc, p, idx, arr) => {
                  if (idx > 0 && (p as number) - (arr[idx - 1] as number) > 1) acc.push('…');
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, i) =>
                  p === '…'
                    ? <Button key={i} size="sm" variant="outline-secondary" disabled>…</Button>
                    : <Button key={i} size="sm"
                        variant={p === currentPage ? 'primary' : 'outline-secondary'}
                        onClick={() => setCurrentPage(p as number)}>{p}</Button>
                )}
              <Button size="sm" variant="outline-secondary" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>›</Button>
              <Button size="sm" variant="outline-secondary" disabled={currentPage === totalPages} onClick={() => setCurrentPage(totalPages)}>»</Button>
            </div>
          </div>
        )}
      </div>

      {/* Bulk Delete Modal */}
      <Modal show={showBulkDeleteModal} onHide={() => setShowBulkDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Bulk Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete <strong>{selectedIds.size} vehicles</strong>? This cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowBulkDeleteModal(false)} disabled={bulkDeleting}>Cancel</Button>
          <Button variant="danger" onClick={handleBulkDelete} disabled={bulkDeleting}>
            {bulkDeleting ? 'Deleting…' : `Delete ${selectedIds.size} Vehicles`}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Delete vehicle <strong>{vehicleToDelete?.plateNumber}</strong>? This cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
          <Button variant="danger" onClick={handleDelete}>Delete</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default VehiclesPage;
